import {
  CURRENCIES,
  DATE_FORMATS,
  FUEL_MIN_SAMPLES_RANGE,
  FUEL_OUTLIER_PERCENT_RANGE,
  FUEL_WINDOW_TRIPS_RANGE,
  LANGUAGES,
  NOTIFICATION_SMS_FALLBACK_MINUTES_RANGE,
  TRIP_DRIVING_HOURS_PER_DAY_RANGE,
  TRIP_REST_HOURS_PER_DAY_RANGE,
  type LngLat,
  type SuggestionWeights,
  type UpdateOrganizationInput,
} from '@chukua/common';
import { Form, Input, InputNumber, Select } from 'antd';
import { CompanyLocationField } from './CompanyLocationField';
import { DispatchCashRuleField } from './DispatchCashRuleField';

const LANGUAGE_LABELS: Record<(typeof LANGUAGES)[number], string> = { en: 'English', sw: 'Swahili' };

export interface CompanySettingsFormValues {
  name: string;
  legalName?: string;
  kraPin?: string;
  address?: string;
  location: LngLat | null;
  currency: UpdateOrganizationInput['currency'];
  timezone: string;
  dateFormat: UpdateOrganizationInput['dateFormat'];
  language: UpdateOrganizationInput['language'];
  countryCode: string;
  jobDueSoonThresholdDays: number;
  creditOverdueThresholdDays: number;
  driverRestHours: number;
  notificationSmsFallbackMinutes: number;
  incidentSmsNumber?: string;
  incidentSmsWaitMinutes: number;
  serviceDueSoonKm: number;
  serviceDueSoonDays: number;
  capacityWarningPercent: number;
  dispatchBlocksUnreconciledCash: boolean;
  suggestionWeights: SuggestionWeights;
  suggestionPageSize: number;
  fuelWindowTrips: number;
  fuelMinSamples: number;
  fuelOutlierPercent: number;
  distanceTolerancePercent: number;
  tripDrivingHoursPerDay: number;
  tripRestHoursPerDay: number;
}

/** Who the company is: short fields paired, two to a row, so the card stays short. */
export function ParticularsFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-2">
      <Form.Item name="name" label="Company name" rules={[{ required: true, message: 'Enter a company name' }]}>
        <Input autoFocus />
      </Form.Item>
      <Form.Item name="legalName" label="Legal name">
        <Input />
      </Form.Item>
      <Form.Item name="kraPin" label="KRA PIN">
        <Input />
      </Form.Item>
      <Form.Item name="currency" label="Reporting currency" rules={[{ required: true }]}>
        <Select options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
      </Form.Item>
      <Form.Item name="countryCode" label="Country code" extra="Two letters, such as KE. Trips into another country count as cross-border.">
        <Input maxLength={2} placeholder="KE" className="sm:max-w-24" />
      </Form.Item>
      <Form.Item name="address" label="Address" className="mb-0 sm:col-span-2">
        <Input.TextArea rows={2} />
      </Form.Item>
    </div>
  );
}

/** How dates, times and text read across the app: three short fields on one row. */
export function LocaleFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-3">
      <Form.Item name="timezone" label="Timezone" className="mb-0" rules={[{ required: true, message: 'Enter a timezone' }]}>
        <Input placeholder="Africa/Nairobi" />
      </Form.Item>
      <Form.Item name="dateFormat" label="Date format" className="mb-0" rules={[{ required: true }]}>
        <Select options={DATE_FORMATS.map((dateFormat) => ({ value: dateFormat, label: dateFormat }))} />
      </Form.Item>
      <Form.Item name="language" label="Default language" className="mb-0" rules={[{ required: true }]}>
        <Select options={LANGUAGES.map((language) => ({ value: language, label: LANGUAGE_LABELS[language] }))} />
      </Form.Item>
    </div>
  );
}

/** Where the company is, on its own card beside the particulars. */
export function LocationFields() {
  return (
    <Form.Item name="location" className="mb-0">
      <CompanyLocationField />
    </Form.Item>
  );
}

/** Tenant-wide thresholds the jobs screens read. */
export function JobsFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-2">
      <Form.Item
        name="jobDueSoonThresholdDays"
        label="Job due-soon threshold (days)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of days' }]}
        extra="How close a job's delivery deadline must be before the jobs list flags it as due soon."
      >
        <InputNumber className="w-full" min={1} max={365} />
      </Form.Item>
      <Form.Item
        name="creditOverdueThresholdDays"
        label="Credit overdue threshold (days)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of days' }]}
        extra="How many days overdue a customer must be before the credit check at job confirmation treats them as badly overdue."
      >
        <InputNumber className="w-full" min={1} max={365} />
      </Form.Item>
    </div>
  );
}

/** Tenant-wide driver rules: the rest window after a trip before the availability status flips back (gitaumoses4/chukua#54). */
/** The windows inside which dispatch's eligibility check warns instead of passing (gitaumoses4/chukua#49). */
export function DispatchFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-3">
      <Form.Item
        name="serviceDueSoonKm"
        label="Service due soon (km)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a distance' }]}
        extra="A truck this close to a service by distance is a warning."
      >
        <InputNumber className="w-full" min={0} max={100000} precision={0} />
      </Form.Item>
      <Form.Item
        name="serviceDueSoonDays"
        label="Service due soon (days)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of days' }]}
        extra="A truck this close to a service by date is a warning."
      >
        <InputNumber className="w-full" min={0} max={365} precision={0} />
      </Form.Item>
      <Form.Item
        name="capacityWarningPercent"
        label="Load warning (% of capacity)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a percentage' }]}
        extra="A load at or above this share of the limit is a warning."
      >
        <InputNumber className="w-full" min={50} max={100} precision={0} />
      </Form.Item>
      <DispatchCashRuleField />
    </div>
  );
}

export function DriversFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-2">
      <Form.Item
        name="driverRestHours"
        label="Driver rest period (hours)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of hours' }]}
        extra="How long a driver shows as resting after a trip is closed out, before they are available again. 0 means no rest period."
      >
        <InputNumber className="w-full" min={0} max={72} precision={0} />
      </Form.Item>
      <Form.Item
        name="notificationSmsFallbackMinutes"
        label="Send by SMS after unseen (minutes)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of minutes' }]}
        extra="A driver whose phone has not called the server for this long is texted instead of pushed. Keep it above the app's 15-minute background sync."
      >
        <InputNumber className="w-full" min={NOTIFICATION_SMS_FALLBACK_MINUTES_RANGE.min} max={NOTIFICATION_SMS_FALLBACK_MINUTES_RANGE.max} precision={0} />
      </Form.Item>
    </div>
  );
}

/** How a truck's rolling fuel consumption is worked out (gitaumoses4/chukua#110). */
export function FuelEconomyFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-3">
      <Form.Item
        name="fuelWindowTrips"
        label="Trips averaged"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of trips' }]}
        extra="The latest trips a truck's figure is worked out over."
      >
        <InputNumber className="w-full" min={FUEL_WINDOW_TRIPS_RANGE.min} max={FUEL_WINDOW_TRIPS_RANGE.max} precision={0} />
      </Form.Item>
      <Form.Item
        name="fuelMinSamples"
        label="Trips needed"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of trips' }]}
        extra="Fewer than this and the truck shows its starting figure instead."
      >
        <InputNumber className="w-full" min={FUEL_MIN_SAMPLES_RANGE.min} max={FUEL_MIN_SAMPLES_RANGE.max} precision={0} />
      </Form.Item>
      <Form.Item
        name="fuelOutlierPercent"
        label="Ignore trips off by (%)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a percentage' }]}
        extra="A trip this far from the middle of the others is left out, as a mis-keyed fill would be."
      >
        <InputNumber className="w-full" min={FUEL_OUTLIER_PERCENT_RANGE.min} max={FUEL_OUTLIER_PERCENT_RANGE.max} precision={0} />
      </Form.Item>
    </div>
  );
}

/** The rest allowance a trip plan's driving time is padded with before it is judged against a deadline (gitaumoses4/chukua#410). */
export function TripPlanningFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-2">
      <Form.Item
        name="tripDrivingHoursPerDay"
        label="Driving hours per rest"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of hours' }]}
        extra="Hours of driving that earn one rest allowance."
      >
        <InputNumber className="w-full" min={TRIP_DRIVING_HOURS_PER_DAY_RANGE.min} max={TRIP_DRIVING_HOURS_PER_DAY_RANGE.max} precision={0} />
      </Form.Item>
      <Form.Item
        name="tripRestHoursPerDay"
        label="Rest hours added"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of hours' }]}
        extra="Added to a trip plan's map time for every block of driving hours, before it is judged against the delivery or empty-return deadline."
      >
        <InputNumber className="w-full" min={TRIP_REST_HOURS_PER_DAY_RANGE.min} max={TRIP_REST_HOURS_PER_DAY_RANGE.max} precision={0} />
      </Form.Item>
    </div>
  );
}

const SUGGESTION_WEIGHT_FIELDS: { key: keyof SuggestionWeights; label: string; extra: string }[] = [
  { key: 'proximity', label: 'Closeness to pickup', extra: 'How near the truck is to the pickup. Counts as neutral until trucks report a position.' },
  { key: 'freeAt', label: 'Free soonest', extra: 'How early the crew is free before the window starts.' },
  { key: 'emptyKm', label: 'Empty kilometres', extra: 'Fewer empty kilometres to the pickup ranks higher. Neutral until trucks report a position.' },
  { key: 'serviceDistance', label: 'Distance to next service', extra: 'More distance left before the truck is due a service ranks higher.' },
  { key: 'familiarity', label: 'Route familiarity', extra: 'A driver who knows the route. Neutral until route history is recorded.' },
  { key: 'coupling', label: 'Already coupled', extra: 'A truck that already pulls the trailer. Neutral until couplings are recorded.' },
];

/** How suggested assignments are ranked: a weight per factor, 0 to 100, and how many suggestions a page shows (gitaumoses4/chukua#52). */
export function SuggestionFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-3">
      {SUGGESTION_WEIGHT_FIELDS.map(({ key, label, extra }) => (
        <Form.Item
          key={key}
          name={['suggestionWeights', key]}
          label={`${label} (weight)`}
          rules={[{ required: true, message: 'Enter a weight' }]}
          extra={extra}
        >
          <InputNumber className="w-full" min={0} max={100} precision={0} />
        </Form.Item>
      ))}
      <Form.Item
        name="suggestionPageSize"
        label="Suggestions per page"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number' }]}
        extra="At least one weight must be above zero."
      >
        <InputNumber className="w-full" min={1} max={100} precision={0} />
      </Form.Item>
    </div>
  );
}
